import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppUpdates } from "@/lib/appUpdatesStore";
import { Sparkles, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, LogOut, User, Briefcase, Calendar, Menu, X,
  MessageSquare, Package, ChevronDown, ChevronRight, Bell,
  Star, BookOpen, Stethoscope, PackageX, Pill, BarChart3, AlertCircle, ClipboardCheck,
  ClipboardList, BookOpenCheck, CalendarCheck
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import NotificationsBell from "@/components/nakes/NotificationsBell";
import MobileStickyHeader from "@/components/layout/MobileStickyHeader";
import PullToRefresh from "@/components/layout/PullToRefresh";
import StokOpnameBanner from "@/components/nakes/StokOpnameBanner";
import NakesSchedulePopup from "@/components/nakes/NakesSchedulePopup";
import NakesTtdRejectedPopup from "@/components/nakes/NakesTtdRejectedPopup";
import CbkQuestionsPopup from "@/components/nakes/CbkQuestionsPopup";
import NakesLocationConsentPopup from "@/components/nakes/NakesLocationConsentPopup";
import { pollGet, runPollTasks, startPolling } from "@/utils/layoutPolling";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Menu Group Component
const MenuGroup = ({ icon: Icon, label, children, badge, isExpanded, onToggle, isActive }) => {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5" />
          <span className="font-medium">{label}</span>
        </div>
        <div className="flex items-center space-x-2">
          {badge > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {badge}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ to, icon: Icon, label, badge, onClick, isActive }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-100 text-blue-700 font-medium"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[18px] text-center">
          {badge}
        </span>
      )}
    </Link>
  );
};

// Standalone Menu Item Component
const StandaloneMenuItem = ({ to, icon: Icon, label, badge, onClick, isActive, highlight }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700 font-medium"
          : highlight
          ? "bg-orange-50 text-orange-700 border border-orange-200"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        <Icon className={`w-5 h-5 ${highlight ? 'text-orange-600' : ''}`} />
        <span className="font-medium">{label}</span>
      </div>
      {badge > 0 && (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center ${
          highlight ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {badge}
        </span>
      )}
    </Link>
  );
};

export default function NakesLayout() {
  const { user, logout } = useAuth();
  const { count: appUpdatesCount } = useAppUpdates();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  
  // Badge counts
  const [readyJobsCount, setReadyJobsCount] = useState(0);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [chatBadgeTotal, setChatBadgeTotal] = useState(0);
  const [pendingSupplyCount, setPendingSupplyCount] = useState(0);
  const [ttdRejected, setTtdRejected] = useState([]);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const [privilegePoints, setPrivilegePoints] = useState(null);
  const [telegramLinked, setTelegramLinked] = useState(true); // optimistic — hide dot until known

  // Collapsible state
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('nakes_sidebar_expanded');
      return saved ? JSON.parse(saved) : { order: true, barang: false, pusatInfo: false, hrd: false };
    } catch {
      return { order: true, barang: false, pusatInfo: false, hrd: false };
    }
  });

  // Save expanded state
  useEffect(() => {
    localStorage.setItem('nakes_sidebar_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand based on route
  useEffect(() => {
    const path = location.pathname;
    
    if (path.includes('/nakes/jobs') || path.includes('/nakes/assignments')) {
      setExpandedGroups(prev => ({ ...prev, order: true }));
    }
    if (path.includes('/nakes/supply') || path.includes('/nakes/logistic') || path.includes('/nakes/my-usage')) {
      setExpandedGroups(prev => ({ ...prev, barang: true }));
    }
    if (path.includes('/nakes/sop') || path.includes('/nakes/tutorial') || path.includes('/nakes/announcements') || path.includes('/nakes/apa-yang-baru') || path.includes('/nakes/bantuan')) {
      setExpandedGroups(prev => ({ ...prev, pusatInfo: true }));
    }
    if (path.includes('/nakes/scorecard') || path.includes('/nakes/warning-letters')) {
      setExpandedGroups(prev => ({ ...prev, hrd: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeSidebar = () => setSidebarOpen(false);

  // Check if daily schedule popup should be shown
  // SKIP for fulltime nakes (May 2026 — fulltime schedule is governed by nurses.off_days, not nakes_schedules)
  const checkDailySchedule = async () => {
    try {
      // Hard-stop for fulltime nakes — popup is for freelance only
      if ((user?.employment_type || '').toLowerCase() === 'fulltime') return;

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/nakes/schedule/daily-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.show_popup) {
        setShowSchedulePopup(true);
      }
    } catch (error) {
      // Silently fail - popup is optional
    }
  };

  // Fetch badge counts
  // Okt 2026 (Fase 3) — enam request dijalankan bersamaan dengan timeout
  // (sebelumnya berurutan tanpa timeout). Mengembalikan false jika semuanya
  // gagal supaya polling melebar jedanya (backoff).
  const fetchCounts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    const headers = { Authorization: `Bearer ${token}` };

    return runPollTasks([
      // Ready jobs count
      async () => {
        const notifRes = await pollGet(`${API}/notifications/summary`, { headers });
        setReadyJobsCount(notifRes.data.ready_jobs_count || 0);
      },
      // Unread chats
      async () => {
        const chatsRes = await pollGet(`${API}/nakes-chats`, { headers });
        const unreadCount = chatsRes.data.filter(c => c.my_unread_count > 0).length;
        setUnreadChatsCount(unreadCount);
      },
      // Feb 2026 — Unified chat badge dari /notifications/badge-summary
      // (konsisten across all roles). Menggantikan sumber `unreadChatsCount`
      // yang hanya menghitung jumlah thread, bukan total pesan belum dibaca.
      async () => {
        const bs = await pollGet(`${API}/notifications/badge-summary`, { headers });
        setChatBadgeTotal(bs.data?.chat?.total || 0);
      },
      // Pending supplies
      async () => {
        const supplyRes = await pollGet(`${API}/supply-requests/pending-confirmation`, { headers });
        setPendingSupplyCount(supplyRes.data.pending_requests?.length || 0);
        // Okt 2026: TTD surat jalan ditolak gudang, dipakai pop up pengingat.
        // Daftar baru hanya mengganti state kalau isinya berubah, supaya tidak render ulang tiap 30 detik.
        const rej = Array.isArray(supplyRes.data.ttd_rejected) ? supplyRes.data.ttd_rejected : [];
        setTtdRejected((prev) => (JSON.stringify(prev) === JSON.stringify(rej) ? prev : rej));
      },
      // Unread announcements
      async () => {
        const announcementsRes = await pollGet(`${API}/knowledge-center/announcements/unread-count`, { headers });
        setUnreadAnnouncementsCount(announcementsRes.data.unread_count || 0);
      },
      // Privilege points - SINGLE SOURCE OF TRUTH
      async () => {
        const privilegeRes = await pollGet(`${API}/nakes/privilege/balance`, { headers });
        setPrivilegePoints(privilegeRes.data.balance);
      },
    ]);
  };

  useEffect(() => {
    // checkDailySchedule is a one-time mount check — NOT part of the polling
    // loop below, so it must not re-fire every time the tab regains focus.
    checkDailySchedule();

    // Okt 2026 (Fase 3) — polling 30 detik lewat startPolling: tidak menumpuk,
    // berhenti saat tab tersembunyi atau offline, backoff saat gagal.
    const stopPolling = startPolling(fetchCounts);

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employment_type]);

  // Telegram link status (for red dot on Profile menu)
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/telegram/link-status`);
        if (!cancelled) setTelegramLinked(!!res.data?.linked);
      } catch {
        if (!cancelled) setTelegramLinked(true); // silent — hide dot
      }
    };
    check();
    const tick = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(tick); };
  }, []);

  // Check active routes
  const isOrderActive = ['/nakes/jobs', '/nakes/assignments'].some(r => location.pathname.startsWith(r));
  const isBarangActive = ['/nakes/supply', '/nakes/logistic', '/nakes/my-usage'].some(r => location.pathname.startsWith(r));
  const isPusatInfoActive = ['/nakes/service-sops', '/nakes/guidelines', '/nakes/tutorial', '/nakes/announcements', '/nakes/apa-yang-baru', '/nakes/bantuan'].some(r => location.pathname.startsWith(r));
  const isChatActive = ['/nakes/chats', '/nakes/coordination'].some(r => location.pathname.startsWith(r));

  return (
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden lg:h-screen" style={{ minHeight: '100dvh' }}>
      {/* Daily Schedule Popup */}
      {showSchedulePopup && (
        <NakesSchedulePopup onClose={() => setShowSchedulePopup(false)} />
      )}

      {/* Okt 2026: pop up TTD surat jalan ditolak gudang (bisa ditutup, muncul lagi tiap 1 jam) */}
      <NakesTtdRejectedPopup items={ttdRejected} />

      {/* Okt 2026: pop up pertanyaan SPV soal barang keluar (muncul lagi tiap 1 jam bila belum dijawab) */}
      <CbkQuestionsPopup />

      {/* Okt 2026: persetujuan lokasi sekali (untuk estimasi tiba saat OTW) + banner izin HP */}
      <NakesLocationConsentPopup />

      {/* Mobile Menu Button - replaced by MobileStickyHeader below */}

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - FIXED on desktop (>=1024px) */}
      {/* Sidebar - FIXED on desktop */}
      <div className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ height: '100dvh', maxHeight: '100dvh' }}>
        {/* Menu Items (sidebar header removed — branding now in MobileStickyHeader) */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto pt-14 lg:pt-4">
          {/* Dashboard */}
          <StandaloneMenuItem
            to="/nakes/dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            onClick={closeSidebar}
            isActive={location.pathname === '/nakes/dashboard'}
          />

          {/* Order - Collapsible */}
          <MenuGroup
            icon={Briefcase}
            label="Order"
            badge={readyJobsCount}
            isExpanded={expandedGroups.order}
            onToggle={() => toggleGroup('order')}
            isActive={isOrderActive}
          >
            <MenuItem
              to="/nakes/jobs/shared"
              icon={Briefcase}
              label="Job Tersedia"
              badge={readyJobsCount}
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/jobs/shared'}
            />
            <MenuItem
              to="/nakes/jobs"
              icon={Briefcase}
              label="Job Saya"
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/jobs' || location.pathname.match(/^\/nakes\/jobs\/[^/]+$/)}
            />
          </MenuGroup>

          {/* Jadwal Saya */}
          <StandaloneMenuItem
            to="/nakes/schedule"
            icon={Calendar}
            label="Jadwal Saya"
            onClick={closeSidebar}
            isActive={location.pathname === '/nakes/schedule'}
          />

          {/* Barang Saya - Collapsible */}
          <MenuGroup
            icon={Package}
            label="Barang Saya"
            badge={pendingSupplyCount}
            isExpanded={expandedGroups.barang}
            onToggle={() => toggleGroup('barang')}
            isActive={isBarangActive}
          >
            <MenuItem
              to="/nakes/supply-requests"
              icon={Package}
              label="Terima Barang"
              badge={pendingSupplyCount}
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/supply-requests')}
            />
            <MenuItem
              to="/nakes/my-usage"
              icon={Pill}
              label="Pemakaian Alat"
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/my-usage'}
            />
            <MenuItem
              to="/nakes/logistic-returns"
              icon={PackageX}
              label="Retur Barang"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/logistic-returns')}
            />
            <MenuItem
              to="/nakes/stok-opname"
              icon={ClipboardCheck}
              label="Stok Opname"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/stok-opname')}
            />
          </MenuGroup>

          {/* HRD - Scorecard & Warning Letters */}
          <MenuGroup
            icon={BarChart3}
            label="Penilaian Kinerja"
            isExpanded={expandedGroups.hrd}
            onToggle={() => toggleGroup('hrd')}
            isActive={location.pathname.startsWith('/nakes/scorecard') || location.pathname.startsWith('/nakes/warning-letters')}
          >
            <MenuItem
              to="/nakes/scorecard"
              icon={BarChart3}
              label="Scorecard Saya"
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/scorecard'}
            />
            <MenuItem
              to="/nakes/warning-letters"
              icon={AlertCircle}
              label="Surat Peringatan"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/warning-letters')}
            />
            {/* Rekap Gaji DISEMBUNYIKAN dari nakes dulu (Nov 2026, penggajian belum rapi).
                Untuk menyalakan lagi: kembalikan MenuItem ke /nakes/rekap-gaji, route di App.js,
                dan ubah hidden: true jadi false di artikel nakes-rekap-gaji. */}
          </MenuGroup>

          {/* Kehadiran — fulltime only (cuti/izin, riwayat kehadiran) */}
          {(user?.employment_type || '').toLowerCase() === 'fulltime' && (
            <StandaloneMenuItem
              to="/nakes/attendance"
              icon={CalendarCheck}
              label="Kehadiran"
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/attendance'}
            />
          )}

          {/* Pusat Info - Collapsible */}
          <MenuGroup
            icon={BookOpen}
            label="Pusat Info"
            badge={unreadAnnouncementsCount + appUpdatesCount}
            isExpanded={expandedGroups.pusatInfo}
            onToggle={() => toggleGroup('pusatInfo')}
            isActive={isPusatInfoActive}
          >
            <MenuItem
              to="/nakes/service-sops"
              icon={ClipboardList}
              label="SOP Layanan"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/service-sops')}
            />
            <MenuItem
              to="/nakes/guidelines"
              icon={BookOpenCheck}
              label="Aturan"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/guidelines')}
            />
            {/* Tutorial DISEMBUNYIKAN dari nakes (Nov 2026): isinya tumpang tindih dengan Pusat Bantuan.
                Untuk menyalakan lagi: kembalikan MenuItem ke /nakes/tutorial dan route di App.js. */}
            <MenuItem
              to="/nakes/bantuan"
              icon={LifeBuoy}
              label="Pusat Bantuan"
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/bantuan'}
            />
            <MenuItem
              to="/nakes/apa-yang-baru"
              icon={Sparkles}
              label="Apa yang Baru"
              badge={appUpdatesCount}
              onClick={closeSidebar}
              isActive={location.pathname === '/nakes/apa-yang-baru'}
            />
            <MenuItem
              to="/nakes/announcements"
              icon={Bell}
              label="Pengumuman"
              badge={unreadAnnouncementsCount}
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/nakes/announcements')}
            />
          </MenuGroup>

          {/* Chat - Unified menu (combines old Chat + Kanal Koordinasi) */}
          <StandaloneMenuItem
            to="/nakes/chats"
            icon={MessageSquare}
            label="Chat"
            badge={chatBadgeTotal || unreadChatsCount}
            onClick={closeSidebar}
            isActive={isChatActive}
          />
        </nav>

        {/* Bottom area: Privilege Points (separate row) + Profile/Bell/Logout (single row) */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          {/* Privilege Points — kept as separate row (Nakes-specific feature) */}
          <Link
            to="/nakes/privilege-history"
            onClick={closeSidebar}
            className="flex items-center justify-between px-3 py-1.5 mb-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg hover:from-amber-100 hover:to-yellow-100 transition-all"
          >
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-xs text-amber-700">Poin Privilege</span>
            </div>
            <span className="text-base font-bold text-amber-800">{privilegePoints ?? '-'}</span>
          </Link>

          {/* Row 1: Profile (flex-1) + Bell pinned right */}
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/nakes/profile"
              onClick={closeSidebar}
              className="flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors relative"
              data-testid="nakes-profile-link"
            >
              <div className="relative flex-shrink-0">
                <User className="w-5 h-5 text-gray-600" />
                {!telegramLinked && (
                  <span
                    className="absolute -top-0.5 -right-0.5 inline-block w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"
                    data-testid="profile-telegram-dot"
                    aria-label="Telegram belum terhubung"
                    title="Telegram belum terhubung"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[10px] text-gray-500 truncate leading-tight">{user?.user_id} · Nakes</p>
              </div>
            </Link>

            {/* Bell pinned right of profile row */}
            <div className="flex-shrink-0" data-testid="sidebar-notification-bell">
              <NotificationsBell />
            </div>
          </div>

          {/* Row 2: Logout full-width */}
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* NOTE: PersistentAlertBanner (Popup Barang Perlu Perhatian) DIHAPUS sesuai requirement */}

      {/* Main Content - Scrollable area, offset by sidebar width on desktop */}
      <div className="flex-1 min-w-0 min-h-screen lg:ml-64 lg:h-screen lg:overflow-y-auto">
        {/* Mobile Sticky Header — Feb 2026 (v2 redesign).
            iter386: hide when inside a chat room. */}
        {!(/^\/nakes\/(chats|job-discussions|coordination-channels|discussions)\/[^/]+/i.test(location.pathname)) && (
          <MobileStickyHeader
            userName={user?.name || 'User'}
            userRole="Nakes"
            sidebarOpen={sidebarOpen}
            onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          />
        )}
        <div className={`w-full h-full overflow-y-auto ${/^\/nakes\/(chats|job-discussions|coordination-channels|discussions)\/[^/]+/i.test(location.pathname) ? '' : 'pt-14 lg:pt-0 pb-24'}`}>
          <StokOpnameBanner />
          <PullToRefresh>
            <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
              <Outlet />
            </div>
          </PullToRefresh>
        </div>
      </div>
    </div>
  );
}
